import BaseAvatar from '@kk/ui/components/BaseAvatar/BaseAvatar';
import Dropdown from '@kk/ui/components/Dropdown/Dropdown';
import type { IconType } from '@kk/ui/components/Icon';
import Icon from '@kk/ui/components/Icon/Icon';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetUserNames } from '@/api/hooks/useAuthorize';
import { logout } from '@/auth';
import MobileMenu from '@/components/Header/elements/MobileMenu';
import { Logo } from '../Logo/Logo';

export function Header() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && search.trim()) {
      navigate(`/search/?q=${encodeURIComponent(search.trim())}`);
    }
  };

  const fullName = useGetUserNames().data?.data?.value;

  const onLogOut = useCallback(() => {
    setMobileMenuOpen(false);
    logout();
  }, [setMobileMenuOpen]);

  const buttonElement = (label: string, icon: string, link: () => void) => (
    <button
      className="typ-body flex w-full items-center justify-between py-2 text-neutral-200"
      onClick={link}
    >
      <span>{label}</span>
      <Icon type={icon as IconType} />
    </button>
  );

  return (
    <>
      <header className="bg-blue-110 fixed z-40 flex h-14 w-full items-center justify-between px-6 text-white md:pr-4">
        <div>
          <div id="error-portal" />
          <div className="flex items-center justify-between">
            <div className="flex h-full w-auto items-center pr-6">
              <Logo />
            </div>
          </div>
        </div>
        <div className="grow flex justify-end">
          <input
            type="search"
            name="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="mx-8 w-[342px] focus:w-full transition-all duration-300 ease-in-out focus:outline-hidden px-2 py-1 bg-white! hover:bg-white! focus:bg-white! text-black! rounded"
          />
        </div>

        <div className="hidden h-full w-auto items-center lg:flex">
          <Dropdown
            className="min-w-50 max-w-62 p-2"
            alignment="start"
            header={
              <header className="after:border-neutral-10 flex flex-col items-center justify-center space-y-2 px-4 pt-2 after:my-2 after:w-full after:border-[0.5px] after:content-['']">
                <BaseAvatar fullName={fullName} />
                <div className="flex flex-col items-center justify-center pb-2">
                  <span className="typ-ui-sm text-neutral-200">{fullName}</span>
                </div>
              </header>
            }
            button={{
              theme: 'primary',
              className: 'bg-blue-110 p-4 focus:kunde:border-blue-100',
              children: (
                <span className="flex items-center gap-2">
                  <span className="typ-button-xs">{fullName}</span>
                  <BaseAvatar size="small" fullName={fullName} />
                </span>
              ),
            }}
            options={[
              {
                children: buttonElement(
                  t('burgerMenu.logout'),
                  'exit',
                  onLogOut,
                ),
              },
              {
                children: (
                  <a
                    href={
                      'https://www.kommunekredit.dk/leasing/om-leasing/leasing/'
                    }
                    className="typ-body flex w-full items-center justify-between py-2 text-neutral-200 underline"
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t('burgerMenu.leasing')}
                  </a>
                ),
              },
            ]}
          />
        </div>
        <div className="flex h-full w-auto items-center lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(true)}
            aria-label={t('openMenu')}
          >
            <Icon type={'burger'} />
          </button>
        </div>
      </header>
      <MobileMenu
        links={[
          // TODO: mqv-ext, unsure if the user should have a home with dashboard features?
          // {
          //   icon: 'home',
          //   title: t('sideMenu.home'),
          //   to: `/`,
          // },
          {
            icon: 'loan-alt',
            title: t('sideMenu.ownAgreements'),
            to: `/agreements/leasingagreements`,
          },
          {
            icon: 'task-done',
            title: t('sideMenu.deliveryApprovals'),
            to: `/deliveryapprovals`,
          },
          {
            icon: 'stopwatch',
            title: t('sideMenu.agreementsToExpiry'),
            to: `/expiringagreements`,
          },
          {
            icon: 'document',
            title: t('sideMenu.annualStatements'),
            to: `/annualstatements`,
          },
          // TODO: mqv-ext, this is not part of the UX - is it intended?
          // {
          //   icon: 'gear' as IconType,
          //   title: t('sideMenu.info'),
          //   to: `/info`,
          // },
        ]}
        menuOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onLogOut={onLogOut}
      />
    </>
  );
}
